import React, { useState, useEffect, useMemo } from 'react';
import { MenuData, MenuItem, CartItem, SelectedOption, ChoiceGroup } from '@/types';
import { 
  ShoppingBag, X, Plus, Minus, ChevronLeft, Check, AlertCircle, 
  Search, Star, Info, MessageSquare, MapPin, Clock, CreditCard
} from 'lucide-react';
import { analyzeMenuHealth } from '@/services/menuHealth';
import { MenuHealthPanel } from './MenuHealthPanel';

// Helper for Image Resolution
const resolveImageUrl = (raw: string | undefined | null) => {
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('/api/assets')) return raw;
  if (raw.includes('.r2.dev/')) return `/api/assets?key=${encodeURIComponent(raw.split('.r2.dev/')[1])}`;
  if (raw.includes('sterlingops-assets/')) return `/api/assets?key=${encodeURIComponent(raw.split('sterlingops-assets/')[1])}`;
  if (raw.startsWith('uploads/')) return `/api/assets?key=${encodeURIComponent(raw)}`;
  if (raw.startsWith('http')) return raw;
  return `data:image/png;base64,${raw}`;
};

interface OrderingPageProps {
  data: MenuData;
  onBack: () => void;
  currency: string; // NEW PROP
  onFixIssue?: (issueKey: string) => void;
  variant?: 'overlay' | 'embedded';
}

export const OrderingPage: React.FC<OrderingPageProps> = ({ data, onBack, currency, onFixIssue, variant = 'overlay' }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [tempOptions, setTempOptions] = useState<SelectedOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>(data.categories[0]?.id || '');
  const health = useMemo(() => analyzeMenuHealth(data), [data]);
  const isEmbedded = variant === 'embedded';

  // Removed local currency state

  // -- Cart Logic --
  const addToCart = () => {
    if (!selectedItem) return;
    const unitPrice = selectedItem.price + tempOptions.reduce((acc, opt) => acc + opt.price, 0);
    const newItem: CartItem = {
      id: `cart-${Date.now()}-${Math.random()}`,
      menuItem: selectedItem,
      selectedOptions: tempOptions,
      quantity: 1,
      unitPrice: unitPrice,
      totalPrice: unitPrice
    };
    setCart(prev => [...prev, newItem]);
    setSelectedItem(null);
    setTempOptions([]);
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty, totalPrice: item.unitPrice * newQty };
      }
      return item;
    }));
  };

  const cartTotal = cart.reduce((acc, item) => acc + item.totalPrice, 0);

  // -- Modal Logic --
  const toggleOption = (group: ChoiceGroup, optionId: string, price: number, name: any) => {
    const existingIdx = tempOptions.findIndex(o => o.optionId === optionId);
    const groupOptions = tempOptions.filter(o => o.groupId === group.id);

    if (existingIdx > -1) {
      setTempOptions(tempOptions.filter(o => o.optionId !== optionId));
    } else {
      if (group.max === 1) {
        const otherOptions = tempOptions.filter(o => o.groupId !== group.id);
        setTempOptions([...otherOptions, { groupId: group.id, optionId, price, name }]);
      } else {
        if (group.max === 0 || groupOptions.length < group.max) {
          setTempOptions([...tempOptions, { groupId: group.id, optionId, price, name }]);
        }
      }
    }
  };

  const isOptionSelected = (optId: string) => tempOptions.some(o => o.optionId === optId);

  const isValidOrder = () => {
    if (!selectedItem) return false;
    for (const group of selectedItem.choiceGroups) {
      const selectedCount = tempOptions.filter(o => o.groupId === group.id).length;
      const effectiveMin = group.required ? Math.max(1, group.min) : group.min;
      if (group.required && selectedCount === 0) return false;
      if (group.min > 0 && selectedCount < group.min) return false;
    }
    return true;
  };

  const currentPrice = selectedItem 
    ? (selectedItem.price + tempOptions.reduce((acc, opt) => acc + opt.price, 0)) 
    : 0;

  const filteredCategories = data.categories.map(cat => ({
    ...cat,
    items: cat.items.filter(item => (item.name.en || '').toLowerCase().includes(searchTerm.toLowerCase()))
  })).filter(cat => cat.items.length > 0);

  const scrollToCategory = (id: string) => {
    setActiveCategory(id);
    const el = document.getElementById(`menu-cat-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={`bg-[#F7F7F7] font-sans text-slate-800 ${isEmbedded ? 'h-full overflow-y-auto' : 'fixed inset-0 z-50 overflow-y-auto'}`}>
      
      {/* Top Navigation Bar */}
      <div className={`bg-white shadow-sm border-b border-slate-200 ${isEmbedded ? '' : 'sticky top-0'} z-30`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
                <button onClick={onBack} className="text-slate-500 hover:text-orange-600 flex items-center gap-2 text-sm font-medium transition-colors">
                    <ChevronLeft size={18} /> Back to Editor
                </button>
                <div className="font-bold text-lg text-slate-800">BistroBuilder Live</div>
                <div className="w-20"></div> {/* Spacer */}
            </div>
        </div>
      </div>

      {/* Restaurant Header Info */}
      <div className="bg-white border-b border-slate-200 pb-0 pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-6 mb-6">
                {/* Logo */}
                <div className="w-24 h-24 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0 shadow-sm">
                    <span className="text-orange-500 font-bold text-xl">LOGO</span>
                </div>
                
                {/* Info */}
                <div className="flex-1">
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">{data.title.en || 'Restaurant Name'}</h1>
                    <div className="text-sm text-slate-500 mb-2 flex items-center gap-4">
                        <span className="flex items-center gap-1"><MapPin size={14}/> Downtown, City</span>
                        <span className="flex items-center gap-1"><Clock size={14}/> Open 24/7</span>
                    </div>
                    <div className="text-xs text-slate-400">
                        Sterling Catering• Min. order: {currency} 0.00
                    </div>
                </div>

                {/* Ratings & Payment */}
                <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-1 text-orange-500 bg-orange-50 px-3 py-1 rounded-full">
                        <Star size={16} fill="currentColor" />
                        <span className="font-bold text-sm">Very good</span>
                    </div>
                    <div className="flex gap-1 opacity-60">
                        <div className="w-8 h-5 bg-slate-200 rounded"></div>
                        <div className="w-8 h-5 bg-slate-200 rounded"></div>
                        <div className="w-8 h-5 bg-slate-200 rounded"></div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-8 border-b border-slate-200">
                <button className="pb-4 px-2 text-orange-600 font-bold border-b-2 border-orange-600 flex items-center gap-2">
                    <ShoppingBag size={18} /> Menu
                </button>
                <button className="pb-4 px-2 text-slate-500 hover:text-slate-800 font-medium flex items-center gap-2">
                    <MessageSquare size={18} /> Reviews
                </button>
                <button className="pb-4 px-2 text-slate-500 hover:text-slate-800 font-medium flex items-center gap-2">
                    <Info size={18} /> Info
                </button>
            </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-8">
            
            {/* LEFT SIDEBAR: Categories */}
            <div className="col-span-2 hidden lg:block">
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden sticky top-24">
                    <div className="p-4 border-b border-slate-100 bg-slate-50 font-bold text-slate-700">Categories</div>
                    <div className="py-2">
                        {data.categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => scrollToCategory(cat.id)}
                                className={`w-full text-left px-4 py-2 text-sm transition-colors border-l-2 ${
                                    activeCategory === cat.id 
                                    ? 'border-orange-500 text-orange-600 bg-orange-50 font-bold' 
                                    : 'border-transparent text-slate-600 hover:bg-slate-50'
                                }`}
                            >
                                {cat.name.en}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* MIDDLE: Menu Items */}
            <div className="col-span-12 lg:col-span-7">
                {/* Search */}
                <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-200 mb-6 flex items-center gap-2">
                    <Search className="ml-2 text-slate-400" size={20} />
                    <input 
                        type="text" 
                        placeholder="Search menu item" 
                        className="flex-1 outline-none text-sm p-2"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Categories & Items */}
                <div className="space-y-6">
                    {filteredCategories.map(cat => (
                        <div key={cat.id} id={`menu-cat-${cat.id}`} className="scroll-mt-24">
                            <div className="flex items-center justify-between bg-slate-100 p-3 rounded-lg mb-4 cursor-pointer hover:bg-slate-200 transition-colors">
                                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                    {cat.name.en} <span role="img" aria-label="fire">🔥</span>
                                </h3>
                                <ChevronLeft className="-rotate-90 text-slate-400" size={20} />
                            </div>

                            <div className="space-y-4">
                                {cat.items.map(item => (
                                                                        <div key={item.id} className="bg-white border border-slate-200 rounded-lg p-4 flex gap-4 hover:shadow-md transition-shadow group relative">
                                                                                {/* Image */}
                                                                                <div className="relative w-24 h-24 bg-slate-100 rounded-md overflow-hidden shrink-0">
                                                                                    {item.image ? (
                                                                                        <img 
                                                                                            src={resolveImageUrl(item.image) || ''} 
                                                                                            alt={item.name.en} 
                                                                                            className="w-full h-full object-cover"
                                                                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; const ph = (e.target as HTMLImageElement).nextElementSibling as HTMLElement; if (ph) ph.classList.remove('hidden'); }}
                                                                                        />
                                                                                    ) : null}

                                                                                    <div className={`absolute inset-0 flex items-center justify-center text-xs text-slate-400 bg-slate-100 ${item.image ? 'hidden' : ''}`}>
                                                                                        No image
                                                                                    </div>
                                                                                </div>

                                        {/* Info */}
                                        <div className="flex-1 flex flex-col justify-center">
                                            <h4 className="font-bold text-slate-800 text-lg mb-1">{item.name.en}</h4>
                                            <p className="text-xs text-slate-500 line-clamp-2 mb-2 leading-relaxed">{item.description.en || 'No description available.'}</p>
                                            <div className="text-orange-600 font-medium text-sm">
                                                {item.price > 0 ? `${currency} ${item.price.toFixed(2)}` : 'Price on Selection'}
                                            </div>
                                        </div>

                                        {/* Add Button */}
                                        <div className="flex flex-col justify-end">
                                            <button 
                                                onClick={() => setSelectedItem(item)}
                                                className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors shadow-sm active:scale-95"
                                            >
                                                <Plus size={18} strokeWidth={3} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* RIGHT SIDEBAR: Health + Cart */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
                <div className="sticky top-24 space-y-4">
                    <MenuHealthPanel analysis={health} onFix={onFixIssue} />
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    {/* Cart Header */}
                    <div className="bg-[#EF5305] text-white p-4 font-bold text-lg">
                        Your Cart
                    </div>

                    <div className="min-h-[200px] max-h-[calc(100vh-200px)] overflow-y-auto bg-[#F9F9F9]">
                        {cart.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                <ShoppingBag size={64} className="mb-4 opacity-20" strokeWidth={1} />
                                <p className="font-medium text-slate-500">There are no items in your cart</p>
                            </div>
                        ) : (
                            <div className="p-4 space-y-4">
                                {cart.map(item => (
                                    <div key={item.id} className="bg-white p-3 rounded border border-slate-200 shadow-sm relative group">
                                        {/* Delete Icon */}
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFromCart(item.id);
                                            }}
                                            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors z-10"
                                        >
                                            <X size={16} />
                                        </button>

                                        <div className="text-sm font-bold text-slate-800 pr-6">{item.menuItem.name.en}</div>
                                        {item.selectedOptions.length > 0 && (
                                            <div className="text-xs text-slate-500 mt-1">
                                                {item.selectedOptions.map(o => o.name.en).join(', ')}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="text-sm font-bold text-slate-700">
                                                {currency} {item.totalPrice.toFixed(2)}
                                            </div>
                                            <div className="flex items-center gap-2 bg-slate-100 rounded px-1">
                                                <button onClick={() => updateQuantity(item.id, -1)} className="p-1 hover:text-orange-600"><Minus size={12}/></button>
                                                <span className="text-xs font-bold w-3 text-center">{item.quantity}</span>
                                                <button onClick={() => updateQuantity(item.id, 1)} className="p-1 hover:text-orange-600"><Plus size={12}/></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                        {cart.length > 0 && (
                            <div className="p-4 bg-white border-t border-slate-200">
                                <div className="flex justify-between items-center mb-4 text-slate-800">
                                    <span className="font-bold">Total</span>
                                    <span className="font-bold text-xl">{currency} {cartTotal.toFixed(2)}</span>
                                </div>
                                <button className="w-full py-3 bg-[#EF5305] hover:bg-[#d14400] text-white font-bold rounded shadow-sm transition-colors">
                                    Checkout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Customization Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header with Image */}
                        <div className="relative h-48 bg-slate-100 shrink-0">
                                {selectedItem.image ? (
                                    <img 
                                        src={resolveImageUrl(selectedItem.image) || ''} 
                                        className="w-full h-full object-cover" 
                                        alt=""
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; const ph = (e.target as HTMLImageElement).nextElementSibling as HTMLElement; if (ph) ph.classList.remove('hidden'); }}
                                    />
                                ) : null}
                                <div className={`absolute inset-0 flex items-center justify-center text-xs text-slate-400 bg-slate-100 ${selectedItem?.image ? 'hidden' : ''}`}>
                                    No image
                                </div>
                <button 
                    onClick={() => { setSelectedItem(null); setTempOptions([]); }}
                    className="absolute top-4 right-4 bg-white p-2 rounded-full text-slate-900 hover:bg-slate-100 transition-colors shadow-sm"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-6 border-b border-slate-100 pb-4">
                    <div className="flex justify-between items-start">
                        <h2 className="text-2xl font-bold text-slate-900 mb-1">{selectedItem.name.en}</h2>
                        <span className="font-bold text-lg text-orange-600">{currency} {selectedItem.price.toFixed(2)}</span>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">{selectedItem.description.en}</p>
                </div>

                <div className="space-y-6">
                    {selectedItem.choiceGroups.map(group => {
                        const currentSelections = tempOptions.filter(o => o.groupId === group.id);
                        const count = currentSelections.length;
                        // Strict check logic
                        const effectiveMin = group.required ? Math.max(1, group.min) : group.min;
                        const isSatisfied = count >= effectiveMin && (group.max === 0 || count <= group.max);
                        
                        return (
                            <div key={group.id} className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                                <div className="flex justify-between items-baseline mb-3">
                                    <h3 className="font-bold text-slate-800">{group.name.en}</h3>
                                    <div className="text-xs">
                                        {group.required && <span className="text-white font-bold bg-orange-500 px-2 py-0.5 rounded mr-2">Required</span>}
                                        <span className="text-slate-400">
                                            {group.max === 1 ? 'Select 1' : (group.max > 1 ? `Max ${group.max}` : '')}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    {group.options.map(opt => {
                                        const isSelected = isOptionSelected(opt.id);
                                        const isDisabled = !isSelected && group.max > 0 && count >= group.max && group.max !== 1;

                                        return (
                                            <label 
                                                key={opt.id}
                                                className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-all bg-white ${
                                                    isSelected 
                                                    ? 'border-orange-500 ring-1 ring-orange-500' 
                                                    : 'border-slate-200 hover:border-orange-300'
                                                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                                                        isSelected ? 'border-orange-500 bg-orange-500 text-white' : 'border-slate-300 bg-white'
                                                    }`}>
                                                        {isSelected && <Check size={12} strokeWidth={3} />}
                                                    </div>
                                                    <span className="font-medium text-sm text-slate-700">{opt.name.en}</span>
                                                </div>
                                                {opt.priceModifier ? (
                                                    <span className="text-sm font-medium text-slate-500">
                                                        +{currency} {opt.priceModifier.toFixed(2)}
                                                    </span>
                                                ) : null}
                                                
                                                <input 
                                                    type="checkbox" 
                                                    className="hidden" 
                                                    checked={isSelected}
                                                    disabled={isDisabled}
                                                    onChange={() => toggleOption(group, opt.id, opt.priceModifier || 0, opt.name)}
                                                />
                                            </label>
                                        );
                                    })}
                                </div>
                                {!isSatisfied && (
                                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1 font-medium">
                                        <AlertCircle size={12} /> 
                                        {group.required && count === 0 ? "Selection required" : `Select at least ${effectiveMin}`}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-white">
                <button 
                    onClick={addToCart}
                    disabled={!isValidOrder()}
                    className="w-full py-4 bg-[#EF5305] text-white rounded font-bold flex items-center justify-between px-6 hover:bg-[#d14400] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md active:scale-[0.99]"
                >
                    <span>Add to Order</span>
                    <span>{currency} {currentPrice.toFixed(2)}</span>
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
